/* eslint-disable no-else-return */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getLocale, useIntl, shallowEqual, useSelector } from 'umi';
import moment from 'moment';
import { Segment, Select } from 'tea-component';
import { formatExeclNormalPercent, formatExeclNum, formatNum, formatPercentNum, getChartDateFormatString, handleDownload } from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import { useWeekDay } from '@/pages/dashboard/utils/customHook';
import { getFunnelTends, getFunnelTendsStep } from '@/pages/dashboard/services/funnel';
import useStepOptions from "@/pages/dashboard/pages/funnel/_config/useStepOptions";
import useFunnelOptionsByFilter from "@/pages/dashboard/pages/funnel/_config/useFunnelOptionsByFilter";

import './index.scss';
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from '@/pages/home/_config/config';
import { getFunnelText } from '../../_config/constant';
import StepSelect from '../StepSelect';
import { POE_DEMO_GAME_CODE_LIST } from '@/pages/dashboard/_config/constant';
import { formatFunnelFilterValues } from '../../_config/utils';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { ITrendStepConfigProps } from './_type';
import ConfigModeTrendStep from './ConfigModeTrendStep';

/**
 * 漏斗趋势步骤组件
 * - 默认模式：使用 funnel 服务（getFunnelTends/getFunnelTendsStep）+ 内置维度选项
 * - configMode=true：完全代理到 ConfigModeTrendStep，使用外部配置化的 metricOptions / dimensionOptions / API
 *
 * NOTE: 由于该函数同时承载两个模式，hooks 必须在条件分支前全部调用以满足 React Hook 规则。
 */
function RevenueChannelDistribution(props: any & ITrendStepConfigProps) {
  const {
    formValue, gameCode: propsGameCode, filterLoading: propsFilterLoading,
    selectorData, hasAddInput, pageType, defaultSelect, firstStepId,
    // configMode props
    configMode, titleConfig, segmentConfig, metricOptions, dimensionOptions: configDimensionOptions,
    apiConfig, requestBuilder, dataExtractor, segmentVal, data: propsData, changeData: propsChangeData,
    noStack, dropdownClassName, metricsInfo: propsMetricsInfo, dropdownStyle,
  } = props;

  // configMode 下从 FilterContext 和 Redux 获取数据
  const contextObj = useContext(FilterContext);
  const reduxGameCode = useSelector((state: any) => state?.dashboard?.currentGame?.game_code, shallowEqual);
  const gameCode = configMode ? reduxGameCode : propsGameCode;
  const filterVal = configMode ? contextObj?.filterVal : null;
  const configFilterLoading = configMode ? contextObj?.filterLoading : false;
  // metricsInfo 优先 props 传入，其次从 data 中获取（PageContainer 会注入 metricsInfo 到 data）
  const metricsInfo = propsMetricsInfo || propsData?.metricsInfo;
  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl();

  // 原有 hooks（必须在任何条件分支之前调用，满足 React hooks 规则）
  const stepOption = useStepOptions(configMode ? {} : formValue);
  const separatedOption = useFunnelOptionsByFilter(configMode ? {} : formValue, false, configMode ? undefined : defaultSelect);
  const [selectedValue, setSelectedValue] = useState<string>('country');
  const [trendsData, setTrendsData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [stepValue, setStepValue] = useState<string>('login_auth_success');
  const [segValue, setSegValue] = useState<string>('conversion');

  // ===================== configMode 分支 =====================
  if (configMode) {
    return (
      <ConfigModeTrendStep
        gameCode={gameCode}
        filterVal={filterVal}
        filterLoading={configFilterLoading}
        titleConfig={titleConfig}
        segmentConfig={segmentConfig}
        metricOptions={metricOptions}
        dimensionOptions={configDimensionOptions}
        apiConfig={apiConfig}
        requestBuilder={requestBuilder}
        dataExtractor={dataExtractor}
        segmentVal={segmentVal}
        data={propsData}
        changeData={propsChangeData}
        noStack={noStack}
        dropdownClassName={dropdownClassName}
        dropdownStyle={dropdownStyle}
        metricsInfo={metricsInfo}
      />
    );
  }

  // ===================== 原有逻辑（保持不变） =====================
  const DEFAULT_STEP_OPTIONS = [
    {
      value: firstStepId,
      text: formatMessage({ id: 'funnel_app_launch' }),
    }
  ];
  const isPoeDemo = POE_DEMO_GAME_CODE_LIST?.includes(gameCode);
  const isRegisterFunnel = window?.location.href?.includes('funnel/registerFunnel');
  const isPoeFunnel = isPoeDemo && isRegisterFunnel;

  const timeType = 'Day';
  const title = isPoeFunnel ? formatMessage({ id: 'funnel_trends_by_step_poe' }): formatMessage({ id: 'funnel_trends_by_step' });
  const segmentOptions: any = [
    { text: formatMessage({ id: "funnel_conversion" }), value: 'conversion' },
    { text: formatMessage({ id: "funnel_count" }), value: 'count' },
  ];
  // 步骤单选根据公共步骤筛选器值可选项配置
  const stepOptions = useMemo(() => {
    const stage = formValue?.filterValue?.stage || [];
    return stepOption?.length === 0 ? DEFAULT_STEP_OPTIONS : stepOption?.filter((v: any) => stage?.includes(v?.value));
  }, [formValue, stepOption]);

  const selectedText = stepOptions?.find((v: any) => v?.value === stepValue)?.text || stepValue;

  useEffect(() => {
    if (propsFilterLoading) return;
    const stage = formValue?.filterValue?.stage || [];
    let step = stepValue;
    if (formValue?.diff) {
      const defaultStepId = 'login_auth_success';
      // 该选项值不在可选值重置为第一项
      if (!stage?.includes(defaultStepId)) {
        setStepValue(stage?.[0]);
        step = stage?.[0];
      } else {
        setStepValue(defaultStepId);
        step = defaultStepId;
      }
    } else {
      // 该选项值不在可选值重置为第一项
      if (!stage?.includes(stepValue)) {
        setStepValue(stage?.[0]);
        step = stage?.[0];
      }
    }
    const params = getRequestParams(formValue, step, selectedValue);
    getTrendsData(params);
  }, [formValue, propsFilterLoading]);

  /**
   * 生成params
   * @param formValue 筛选器参数
   * @param stepValue  步骤筛选器值
   * @param selectedValue 维度下拉值
   * @returns 
   */
  const getRequestParams = (formValue, stepValue, selectedValue) => {
    const { filterValue, newFilter, diff } = formValue || {};
    const { rangeTime, ...filters } = filterValue || {};
    let startTime = filterValue?.rangeTime?.[0];
    const endTime = filterValue?.rangeTime?.[1];

    if (newFilter && diff) {
      switch (timeType) {
        case "Day": {
          startTime = moment().subtract(7, 'day').format('YYYY-MM-DD');
          break;
        }
      }
    }

    const params = {
      game_code: gameCode,
      period: 'daily',
      start_time: moment(startTime).format("YYYY-MM-DD"),
      end_time: moment(endTime).format("YYYY-MM-DD"),
      filters: { ...formatFunnelFilterValues(filters), stage_list: stepValue ? [stepValue] : [] },
    };
    if (selectedValue === 'accumulated') {
      return params;
    } else {
      const accumulation: any = {
        by_zone: selectedValue === 'zone',
        by_region: selectedValue === 'region',
        by_country: selectedValue === 'country',
        by_channel: selectedValue === 'channel',
        by_system: selectedValue === 'system',
        by_language: selectedValue === 'language',
        by_app_version: selectedValue === 'app_version',
        by_network_type: selectedValue === 'network_type',
        by_memory: selectedValue === 'memory',
      };
      if (hasAddInput) accumulation.by_store_channel = selectedValue === 'store_channel';
      if (filters?.ua_network) accumulation.by_ua_network = selectedValue === 'ua_network';
      if (filters?.campaign) accumulation.by_campaign = selectedValue === 'campaign';
      if(filters?.ab_test) accumulation.by_ab_test = selectedValue === 'ab_test';
      return { ...params, accumulation };
    }
  };


  const getTrendsData = (params: any) => {
    setError(false);
    setLoading(true);
    if (!params?.accumulation) {
      getFunnelTends(params, pageType).then((res) => {
        setLoading(false);
        setTrendsData(res);
      }).catch(() => {
        setLoading(false);
        setError(true);
      });
    } else {
      getFunnelTendsStep(params, pageType).then((res) => {
        setLoading(false);
        setTrendsData(res);
      }).catch(() => {
        setLoading(false);
        setError(true);
      });
    }
  };

  const changeDataByChartType = (trendsData: any, segValue: any) => {
    const xAxisData: any = trendsData?.map((item: any) => item?.date);
    const legendData: any = [selectedText];
    const data: any = [];
    trendsData?.map((v: any) => {
      const metric = v?.metrics?.[`${stepValue  }_${  segValue}`];
      data?.push(metric?.value ?? '');
    });
    const lineSeries: any = [{
      name: selectedText,
      data,
      type: 'line',
      symbol: 'circle',
    }];
    const barSeries: any = [{
      name: selectedText,
      data,
      type: 'bar',
      ...(!noStack ? { stack: 'bar' } : {}),
      symbol: 'circle',
      barGap: '5%',
      barCategoryGap: '50%',
    }];
    return {
      xAxisData,
      legendData,
      lineSeries,
      barSeries,
    };
  };

  const changeDataByDimension = (trendsData: any, segValue: any) => {
    const xAxisData: any = [...new Set(trendsData?.map((item: any) => item?.date))];
    const legendData: any = [...new Set(trendsData?.map((item: any) => getFunnelText(selectedValue, item?.[selectedValue], gameCode, getLocale(), selectorData)))];
    const lineSeries: any = [];
    const barSeries: any = [];
    legendData?.map((item: any) => {
      const data: any = [];
      trendsData?.map((cItem: any) => {
        if (item === getFunnelText(selectedValue, cItem[selectedValue], gameCode, getLocale(), selectorData)) {
          const metric = cItem?.metrics?.[`${stepValue  }_${  segValue}`];
          data.push(metric?.value ?? '');
        }
      });
      lineSeries?.push({
        name: item,
        data,
        type: 'line',
        symbol: 'circle',
      });
      const seriesItem: any = {
        name: item,
        data,
        type: 'bar',
        symbol: 'circle',
        barGap: '5%',
        barCategoryGap: '50%',
      };

      if (!noStack && (data?.length > 7 || segValue === 'count')) seriesItem.stack = 'bar';
      barSeries?.push(seriesItem);
    });

    return {
      xAxisData,
      legendData,
      lineSeries,
      barSeries,
    };
  };

  const { xAxisData, legendData, lineSeries, barSeries } = useMemo(() => {
    return selectedValue === 'accumulated' ? changeDataByChartType(trendsData, segValue) : changeDataByDimension(trendsData, segValue);
  }, [trendsData, segValue, getLocale()]);
  const isNodata = lineSeries?.filter((item: any) => item?.data?.length)?.length === 0;

  const options = useMemo(() => {
    const dateColumn = {
      key: 'date',
      align: 'left',
      fixed: 'left',
      width: 160,
      header: formatMessage({ id: 'common_date' }),
      render: (cvm: any) => {
        const weekDay = getWeekDay(cvm?.date);
        const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
        return <>
          <span className='table-text'>{getChartDateFormatString(timeType, cvm?.date, true)}</span>
          {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
        </>;
      },
    };
    if (selectedValue === 'accumulated') {
      const tableOption = [
        dateColumn,
        {
          key: stepValue,
          align: 'right',
          width: 120 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
          header: stepOptions?.find((v: any) => v?.value === stepValue)?.text || stepValue,
        }
      ];
      const tableData: any = [];
      const downloadData: any = [];
      // 表数据
      trendsData?.map((item: any) => {
        const obj: any = { date: item?.date };
        const downObj: any = { date: item?.date };
        const metric = item?.metrics?.[`${stepValue  }_${  segValue}`];
        const metricValue = segValue === 'conversion' ? formatPercentNum(metric?.value) : formatNum(parseFloat(metric?.value), false, true);
        obj[stepValue] = metricValue ?? '-';
        downObj[stepValue] = segValue === 'conversion' ? formatExeclNormalPercent(metric?.value ?? '-') : formatExeclNum(metric?.value ?? '-');
        tableData.push(obj);
        downloadData.push(downObj);
      });

      return {
        table: {
          option: tableOption,
          data: tableData,
          downloadData
        },
      };
    } else {
      const legendData: any = [...new Set(trendsData?.map((item: any) => getFunnelText(selectedValue, item?.[selectedValue], gameCode, getLocale(), selectorData)))];
      const dimensionColumn = legendData?.map((item: any) => {
        return {
          key: item,
          align: 'right',
          width: 120 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
          header: item,
        };
      });
      const tableOption = [dateColumn, ...dimensionColumn];
      const tableData: any = [];
      const downloadData: any = [];
      // 表数据
      trendsData?.map((item: any) => {
        const obj: any = { date: item?.date };
        const downObj: any = { date: item?.date };
        legendData?.map((cItem: any) => {
          if (cItem === getFunnelText(selectedValue, item?.[selectedValue], gameCode, getLocale(), selectorData)) {
            const metric = item?.metrics?.[`${stepValue  }_${  segValue}`];
            const metricValue = segValue === 'conversion' ? formatPercentNum(metric?.value) : formatNum(parseFloat(metric?.value), false, true);
            const dateItem = tableData?.find((v: any) => v?.date === item?.date);
            if (dateItem) {
              dateItem[cItem] = metricValue ?? '-';
            } else {
              obj[cItem] = metricValue ?? '-';
              tableData.push(obj);
            }
            const downDateItem = downloadData?.find((v: any) => v?.date === item?.date);
            if (downDateItem) {
              downDateItem[cItem] = segValue === 'conversion' ? formatExeclNormalPercent(metric?.value ?? '-') : formatExeclNum(metric?.value ?? '-');
            } else {
              downObj[cItem] = segValue === 'conversion' ? formatExeclNormalPercent(metric?.value ?? '-') : formatExeclNum(metric?.value ?? '-');
              downloadData.push(downObj);
            }
          }
        });
      });
      return {
        table: {
          option: tableOption,
          data: tableData,
          downloadData
        },
      };
    }
  }, [trendsData, stepValue, segValue, selectedValue, getLocale()]);

  const formatterFunc = (params: any, timeType = 'Day', segValue: any) => {
    const weekDay = getWeekDay(params[0].axisValue);
    return getEchartTooltipText({
      title: `${moment(`${params[0].axisValue}`).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD')  }(${  weekDay  })`,
      metricsName: formatMessage({ id: `funnel_${segValue}` }),
      data: [...params]
        // .sort((f, s) => s.value - f.value)
        .map(({ seriesName, value, color }) => ({
          name: seriesName,
          metricsNum: segValue === 'conversion' ? formatPercentNum(value) : formatNum(parseFloat(value), false, true),
          color,
        })),
      // isRemoveNull: true,
      isSort: true
    });
  };

  const renderList = [{
    type: 'barChart',
    svgType: 'column-chart',
    content: formatMessage({ id: 'common_bar_chart' }),
    component: <BarChart
      height={360}
      legendData={xAxisData.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData.length > 0 ? barSeries : []}
      formatterFunc={(params: any) => formatterFunc(params, timeType, segValue)}
      setOtherConfigFunc={(opt: any) => {
        opt.xAxis[0].axisLabel.formatter = (value) => getChartDateFormatString(timeType, value);
        opt.yAxis.axisLabel.formatter = (value: any) => {
          const metricValue = segValue === 'conversion' ? formatPercentNum(value) : formatNum(parseFloat(value), false, true);
          return metricValue;
        };
        opt = xAxisHighLight(opt, {
          formatter: (params: { value: string; }) => {
            return getChartDateFormatString(timeType, params.value);
          }
        });
        return opt;
      }}
    />
  },
  {
    type: 'lineChart',
    svgType: 'discount-chart',
    content: formatMessage({ id: 'common_line_chart' }),
    component: <LineChart
      height={360}
      legendData={xAxisData.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData.length > 0 ? lineSeries : []}
      formatterFunc={(params: any) => formatterFunc(params, timeType, segValue)}
      setOtherConfigFunc={(opt: any) => {
        opt.xAxis[0].axisLabel.formatter = (value) => getChartDateFormatString(timeType, value);
        opt.yAxis.axisLabel.formatter = (value: any) => {
          const metricValue = segValue === 'conversion' ? formatPercentNum(value) : formatNum(parseFloat(value), false, true);
          return metricValue;
        };
        opt = xAxisHighLight(opt, {
          formatter: (params: { value: string; }) => {
            return getChartDateFormatString(timeType, params.value);
          }
        });
        return opt;
      }}
    />
  },
  {
    type: 'sheet',
    svgType: 'sheet',
    content: formatMessage({ id: 'common_table' }),
    component: <MiniTable
      height={360}
      loading={false}
      tableOption={options?.table?.option}
      tableList={options?.table?.data}
    />
  }];

  return <CardBox
    className='dbd-trends-step'
    height={360}
    bordered={false}
    titleCom={title}
    filterSlot={
      <div>
        <div className='dbd-trends-flex'>
          <Segment
            className="step-flex-segment"
            rimless={false}
            value={segValue}
            onChange={(value) => setSegValue(value)}
            options={segmentOptions}
          />
          <StepSelect
            options={stepOptions}
            stepValue={stepValue}
            onChange={(value: any) => {
              setStepValue(value);
              const params = getRequestParams(formValue, value, selectedValue);
              getTrendsData(params);
            }}
          />
        </div>
        <Select
          listWidth={180}
          listHeight={226}
          appearance="default"
          className="overlay-line-select-text"
          options={separatedOption}
          value={selectedValue}
          onChange={(value) => {
            setSelectedValue(value);
            const params = getRequestParams(formValue, stepValue, value);
            getTrendsData(params);
          }}
          placeholder="accumulated"
        />
      </div>
    }
    renderList={renderList || []}
    defaultAvtive={1}
    downloadClick={async () => {
      handleDownload(options?.table, title);
    }}
    isLoading={loading}
    isError={error}
    isNodata={isNodata}
  />;
}

export default RevenueChannelDistribution;
